/**
 * "Reflection & review" — three visibility toggles (design/screens/
 * objective-create-desktop.md SS7.8, R3.14). Exactly three rows: no fourth
 * "Private" toggle exists anywhere in this markup (AC-O23) — not disabled,
 * not a placeholder, simply absent.
 */
export function VisibilityTogglesSection({
  includeInWeeklyReview,
  surfaceOnToday,
  ambientRewardOnComplete,
  onChange,
}: {
  readonly includeInWeeklyReview: boolean;
  readonly surfaceOnToday: boolean;
  readonly ambientRewardOnComplete: boolean;
  readonly onChange: (changes: {
    readonly includeInWeeklyReview?: boolean;
    readonly surfaceOnToday?: boolean;
    readonly ambientRewardOnComplete?: boolean;
  }) => void;
}) {
  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-5)', borderTop: '1px solid var(--border-subtle)' }}>
      <span
        style={{
          fontSize: 'var(--type-label-size)',
          fontWeight: 'var(--type-label-weight)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        Reflection &amp; review
      </span>

      <ToggleRow
        label="Include in weekly review"
        description="Shows up in your Sunday review while this is on."
        checked={includeInWeeklyReview}
        onChange={(next) => onChange({ includeInWeeklyReview: next })}
      />
      <ToggleRow
        label="Surface on Today"
        description="Lets this objective's actions appear in your daily due/next-up list."
        checked={surfaceOnToday}
        onChange={(next) => onChange({ surfaceOnToday: next })}
      />
      <ToggleRow
        label="Ambient reward on complete"
        description="Completing an action here earns XP, same as any other objective."
        checked={ambientRewardOnComplete}
        onChange={(next) => onChange({ ambientRewardOnComplete: next })}
      />
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly description: string;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>{label}</div>
        <div style={{ marginTop: '2px', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>{description}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        style={{
          flex: 'none',
          width: '40px',
          height: '24px',
          borderRadius: 'var(--radius-pill)',
          background: checked ? 'var(--hue-primary-vivid)' : 'var(--border-strong)',
          position: 'relative',
          transition: 'background var(--dur-fast) var(--ease-standard)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '2px',
            left: checked ? '18px' : '2px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'var(--surface-raised)',
            transition: 'left var(--dur-fast) var(--ease-standard)',
          }}
        />
      </button>
    </div>
  );
}
